import React, { useState, useEffect } from 'react';
import { Table, Card, Tag, Button, message, Space, Popconfirm, Typography, Row, Col, DatePicker, Tabs, Modal, Form, Input, InputNumber, Select, Grid } from 'antd';
import { DeleteOutlined, ReloadOutlined, DollarCircleOutlined, LineChartOutlined, CalendarOutlined, PlusOutlined } from '@ant-design/icons';
import ExportButton from '../components/ExportButton';
import client from '../api/client';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const PaymentsPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user } = useAuth();
    const isStaff = user?.role === 'STAFF';
    const [activeTab, setActiveTab] = useState('collections');
    const [payments, setPayments] = useState([]);
    const [purchasePayments, setPurchasePayments] = useState([]);
    const [purchasePayables, setPurchasePayables] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dates, setDates] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
    const [isPurchaseModalVisible, setIsPurchaseModalVisible] = useState(false);
    const [purchaseForm] = Form.useForm();

    useEffect(() => {
        if (activeTab === 'collections') {
            fetchPayments();
        } else {
            fetchPurchasePayments();
        }
    }, [dates, activeTab]);

    const fetchPayments = async () => {
        setLoading(true);
        try {
            const params = {};
            if (dates) {
                params.start_date = dates[0].format('YYYY-MM-DD');
                params.end_date = dates[1].format('YYYY-MM-DD');
            }
            const { data } = await client.get('/transactions/payments', { params });
            setPayments(data);
        } catch (error) {
            message.error('Failed to fetch payments');
        } finally {
            setLoading(false);
        }
    };

    const fetchPurchasePayments = async () => {
        setLoading(true);
        try {
            const params = {};
            if (dates) {
                params.start_date = dates[0].format('YYYY-MM-DD');
                params.end_date = dates[1].format('YYYY-MM-DD');
            }
            const { data } = await client.get('/transactions/purchase-payments', { params });
            setPurchasePayments(data);
        } catch (error) {
            message.error('Failed to fetch purchase payments');
        } finally {
            setLoading(false);
        }
    };

    const fetchPurchasePayables = async () => {
        try {
            const { data } = await client.get('/transactions/purchases');
            const openPayables = (data || []).filter((purchase) => purchase.status === 'POSTED' && Number(purchase.balance || 0) > 0);
            setPurchasePayables(openPayables);
        } catch (error) {
            message.error('Failed to load purchase payables');
        }
    };


    const handleDelete = async (id) => {
        try {
            if (activeTab === 'collections') {
                await client.delete(`/transactions/payments/${id}`);
                message.success('Payment deleted successfully');
                fetchPayments();
            } else {
                await client.delete(`/transactions/purchase-payments/${id}`);
                message.success('Purchase payment deleted successfully');
                fetchPurchasePayments();
            }
        } catch (error) {
            message.error('Failed to delete payment');
        }
    };

    const openPurchaseModal = async () => {
        await fetchPurchasePayables();
        purchaseForm.resetFields();
        purchaseForm.setFieldsValue({
            payment_number: `PPAY-${dayjs().valueOf()}`,
            payment_date: dayjs(),
            payment_method: 'CASH',
        });
        setIsPurchaseModalVisible(true);
    };

    const handleCreatePurchasePayment = async (values) => {
        try {
            await client.post('/transactions/purchase-payments', {
                ...values,
                payment_date: values.payment_date.format('YYYY-MM-DD HH:mm:ss'),
            });
            message.success('Supplier payment recorded');
            setIsPurchaseModalVisible(false);
            purchaseForm.resetFields();
            fetchPurchasePayments();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to record supplier payment');
        }
    };

    const StatCard = ({ title, value, icon, color, subText, prefix = "₱" }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {prefix}{parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    const collectionColumns = [
        {
            title: 'Payment #',
            dataIndex: 'payment_number',
            key: 'payment_number',
            render: (text) => <Typography.Text strong>{text}</Typography.Text>
        },
        {
            title: 'Date',
            dataIndex: 'payment_date',
            key: 'payment_date',
            render: (date) => dayjs(date).format('MMM DD, YYYY')
        },
        {
            title: 'Invoice Ref',
            dataIndex: ['invoice', 'invoice_number'],
            key: 'invoice_number',
            render: (text) => <Tag color="blue">{text}</Tag>
        },
        {
            title: 'Partner',
            key: 'partner_name',
            render: (_, record) => record.customer?.name || record.invoice?.customer?.name || 'N/A'
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (amount) => <Typography.Text strong style={{ color: '#10b981' }}>₱{parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography.Text>
        },
        {
            title: 'Method',
            dataIndex: 'payment_method',
            key: 'payment_method',
            render: (method) => <Tag style={{ borderRadius: 6 }}>{method}</Tag>
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Popconfirm
                    title="Delete payment?"
                    description="This will update the invoice balance."
                    onConfirm={() => handleDelete(record.id)}
                    okText="Yes"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                >
                    <Button icon={<DeleteOutlined />} danger size="small" />
                </Popconfirm>
            )
        },
    ].filter(col => !(isStaff && col.key === 'action'));

    const payableColumns = [
        {
            title: 'Payment #',
            dataIndex: 'payment_number',
            key: 'payment_number',
            render: (text) => <Typography.Text strong>{text}</Typography.Text>
        },
        {
            title: 'Date',
            dataIndex: 'payment_date',
            key: 'payment_date',
            render: (date) => dayjs(date).format('MMM DD, YYYY')
        },
        {
            title: 'Purchase Ref',
            dataIndex: ['purchase', 'ticket_number'],
            key: 'purchase_ref',
            render: (text) => <Tag color="purple">{text}</Tag>
        },
        {
            title: 'Supplier',
            key: 'supplier',
            render: (_, record) => record.supplier?.name || record.purchase?.partner?.name || 'N/A'
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (amount) => <Typography.Text strong style={{ color: '#ef4444' }}>₱{parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography.Text>
        },
        {
            title: 'Method',
            dataIndex: 'payment_method',
            key: 'payment_method',
            render: (method) => <Tag style={{ borderRadius: 6 }}>{method}</Tag>
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Popconfirm
                    title="Delete purchase payment?"
                    description="This will update the supplier balance."
                    onConfirm={() => handleDelete(record.id)}
                    okText="Yes"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                >
                    <Button icon={<DeleteOutlined />} danger size="small" />
                </Popconfirm>
            )
        },
    ].filter(col => !(isStaff && col.key === 'action'));

    const totalVolume = (activeTab === 'collections' ? payments : purchasePayments)
        .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const avgPayment = (activeTab === 'collections' ? payments : purchasePayments).length
        ? totalVolume / (activeTab === 'collections' ? payments : purchasePayments).length
        : 0;
    const todayTotal = (activeTab === 'collections' ? payments : purchasePayments)
        .filter(p => dayjs(p.payment_date).isSame(dayjs(), 'day'))
        .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

    const tableData = activeTab === 'collections' ? payments : purchasePayments;
    const columns = activeTab === 'collections' ? collectionColumns : payableColumns;

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Payment Journals</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Track collections and supplier payouts in one place.</Typography.Text>
                </div>
                <Space wrap>
                    <RangePicker
                        value={dates}
                        onChange={(val) => setDates(val)}
                        style={{ borderRadius: 8, width: isMobile ? '100%' : undefined }}
                    />
                    <ExportButton
                        filename={activeTab === 'collections' ? 'payments-collections' : 'payments-payables'}
                        sheetName="Payments"
                        title={activeTab === 'collections' ? 'Payment Collections' : 'Supplier Payables'}
                        headers={activeTab === 'collections'
                            ? ['Payment #', 'Date', 'Invoice Ref', 'Partner', 'Amount', 'Method']
                            : ['Payment #', 'Date', 'Purchase Ref', 'Supplier', 'Amount', 'Method']}
                        getData={() => (activeTab === 'collections' ? payments : purchasePayments).map(p =>
                            activeTab === 'collections' ? [
                                p.payment_number,
                                dayjs(p.payment_date).format('YYYY-MM-DD'),
                                p.invoice?.invoice_number || '',
                                p.customer?.name || p.invoice?.customer?.name || '',
                                p.amount,
                                p.payment_method,
                            ] : [
                                p.payment_number,
                                dayjs(p.payment_date).format('YYYY-MM-DD'),
                                p.purchase?.ticket_number || '',
                                p.supplier?.name || p.purchase?.partner?.name || '',
                                p.amount,
                                p.payment_method,
                            ]
                        )}
                        style={{ borderRadius: 8 }}
                    />
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => (activeTab === 'collections' ? fetchPayments() : fetchPurchasePayments())}
                        style={{ borderRadius: 8 }}
                    >
                        Refresh
                    </Button>
                    {activeTab === 'payables' && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openPurchaseModal}
                            style={{ borderRadius: 8 }}
                        >
                            Record Supplier Payment
                        </Button>
                    )}
                </Space>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                    { key: 'collections', label: 'Collections' },
                    { key: 'payables', label: 'Payables' },
                ]}
                style={{ marginBottom: 24 }}
            />

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col span={8}>
                    <StatCard
                        title={activeTab === 'collections' ? 'Total Collections' : 'Total Payables Paid'}
                        value={totalVolume}
                        icon={<DollarCircleOutlined />}
                        color={activeTab === 'collections' ? '#10b981' : '#ef4444'}
                        subText="MTD cash movement"
                    />
                </Col>
                <Col span={8}>
                    <StatCard
                        title="Avg Payment"
                        value={avgPayment}
                        icon={<LineChartOutlined />}
                        color="#3b82f6"
                        subText={`From ${(activeTab === 'collections' ? payments : purchasePayments).length} transactions`}
                    />
                </Col>
                <Col span={8}>
                    <StatCard
                        title="Today's Total"
                        value={todayTotal}
                        icon={<CalendarOutlined />}
                        color="#f59e0b"
                        subText="Real-time total"
                    />
                </Col>
            </Row>

            <Card className="premium-card">
                <Table
                    dataSource={tableData}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 1000 } : undefined}
                    pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                />
            </Card>

            <Modal
                title="Record Supplier Payment"
                open={isPurchaseModalVisible}
                onCancel={() => setIsPurchaseModalVisible(false)}
                footer={null}
                width={isMobile ? '100%' : 600}
                style={{ top: isMobile ? 12 : 24 }}
                bodyStyle={{ padding: isMobile ? 12 : 24 }}
            >
                <Form form={purchaseForm} layout="vertical" onFinish={handleCreatePurchasePayment}>
                    <Form.Item name="purchase_id" label="Purchase Ticket" rules={[{ required: true }]}>
                        <Select
                            showSearch
                            optionFilterProp="children"
                            placeholder="Select purchase"
                        >
                            {purchasePayables.map((purchase) => (
                                <Select.Option key={purchase.id} value={purchase.id}>
                                    {purchase.ticket_number} - {purchase.partner?.name} (Bal: ₱{Number(purchase.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })})
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="payment_number" label="Payment #" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="payment_date" label="Payment Date" rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} showTime />
                    </Form.Item>
                    <Form.Item shouldUpdate={(prev, curr) => prev.purchase_id !== curr.purchase_id}>
                        {({ getFieldValue }) => {
                            const purchaseId = getFieldValue('purchase_id');
                            const selectedPurchase = purchasePayables.find((purchase) => purchase.id === purchaseId);
                            const maxAmount = Number(selectedPurchase?.balance || 0);

                            return (
                                <Form.Item
                                    name="amount"
                                    label="Amount"
                                    rules={[
                                        { required: true },
                                        {
                                            validator: (_, value) => {
                                                if (value === undefined || value === null || value === '') return Promise.resolve();
                                                if (value <= 0) return Promise.reject(new Error('Amount must be greater than 0.'));
                                                if (maxAmount > 0 && value > maxAmount) {
                                                    return Promise.reject(new Error(`Amount cannot exceed balance (₱${maxAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}).`));
                                                }
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <InputNumber style={{ width: '100%' }} min={0.01} max={maxAmount || undefined} />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                    <Form.Item name="payment_method" label="Method" rules={[{ required: true }]}>
                        <Select>
                            <Select.Option value="CASH">Cash</Select.Option>
                            <Select.Option value="CHECK">Check</Select.Option>
                            <Select.Option value="BANK_TRANSFER">Bank Transfer</Select.Option>
                            <Select.Option value="EWALLET">E-Wallet</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="reference_number" label="Reference #">
                        <Input />
                    </Form.Item>
                    <Form.Item name="remarks" label="Remarks">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>
                            Submit Payment
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PaymentsPage;

